/**
 * LE TRANSPORT vers le BFF (RFC §17/§18, BBG-208 / E4-T1).
 *
 * ─── Ce que ce module NE FAIT PAS ────────────────────────────────────────────
 *
 * Il ne rafraîchit rien, ne coordonne rien, ne met rien en cache de lui-même.
 * Le renouvellement des preuves est un détail du BFF et du moteur — l'exposer
 * ici inviterait un appelant à le piloter, donc à recréer la coordination que
 * le modèle supprime (c'est pour cela qu'aucune méthode de renouvellement ne
 * sera JAMAIS publique, y compris dans les versions suivantes).
 *
 * ─── Le triptyque de chaque écriture ─────────────────────────────────────────
 *
 * Le BFF exige sur toute requête non-GET : l'Origin (posée par le navigateur,
 * non modifiable), le cookie trousseau (posé par le navigateur, HttpOnly),
 * et le double-submit (la page recopie le jeton CSRF dans l'en-tête). La
 * LECTURE de ce jeton est INJECTÉE : le cookie est délibérément lisible par la
 * page (c'est le mécanisme même du double-submit), mais ce paquet ne lit jamais
 * les cookies par l'API de la page — critère d'acceptation, vérifié par la
 * porte RT-01, qui en possède le nom exact : ce fichier ne peut pas l'écrire
 * sans faire rougir la suite. C'est l'appelant — la page du portail — qui
 * fournit `lireJetonCsrf`.
 */
/** Un refus du BFF, transporté tel quel : le code est le contrat du client (RFC §17). */
export class ErreurPortail extends Error {
    statut;
    code;
    constructor(statut, code, message) {
        super(message);
        this.name = 'ErreurPortail';
        this.statut = statut;
        this.code = code;
    }
}
/**
 * Une origine de BFF acceptable : http(s) absolue, SANS chemin ni requête.
 * Tronquer en silence un chemin fourni (gateway déployée sous /bff/) serait
 * le raccourci que ce dépôt refuse : on rejette, on ne « corrige » pas.
 */
function validerOrigineBff(origine) {
    let url;
    try {
        url = new URL(origine);
    }
    catch {
        throw new ErreurPortail(0, 'origine_bff_invalide', `Origine BFF illisible : ${origine}`);
    }
    const estHttp = url.protocol === 'https:' || url.protocol === 'http:';
    const estNue = url.pathname === '/' && url.search === '' && url.hash === '';
    if (!estHttp || !estNue) {
        throw new ErreurPortail(0, 'origine_bff_invalide', `Origine BFF refusée : une origine http(s) exacte est attendue, sans chemin (${origine}).`);
    }
    return url.origin;
}
/**
 * Un transport qui parle au BFF, de cette origine ou d'une origine SŒUR.
 *
 * Sans `origineBff` : même origine STRICTE (`same-origin`) — le cookie est
 * `SameSite=Strict` et sans `Domain`, rien ne doit tenter de suivre ailleurs.
 *
 * Avec : le trousseau est MUTUALISÉ entre les applications du tenant (back-
 * office, Mon compte, kiosque — une seule liste, partout). La page vit sur sa
 * propre origine et parle au BFF par-dessus : `include` laisse le navigateur
 * joindre le trousseau, et rien d'autre ne le pose. Ce croisement n'existe
 * que si le BFF a la page dans sa liste d'origines sœurs (le cookie Strict
 * est same-SITE — le port est exclu de la notion de site) : une origine que
 * le BFF n'a pas autorisée obtient le refus CORS du BFF, pas un secret.
 */
export function transportBff(lireJetonCsrf, origineBff) {
    const base = origineBff === undefined ? null : validerOrigineBff(origineBff);
    return {
        async appel(methode, chemin, options = {}) {
            // Le périmètre est borné ICI : un appelant ne peut pas détourner ce
            // transport vers une route hors du BFF (une future régression
            // l'enverrait sinon sur le moteur avec les mêmes cookies). Le contrôle
            // porte sur le CHEMIN, avant tout joint avec la base — une origine
            // fournie n'élargit rien.
            if (!chemin.startsWith('/portail/')) {
                throw new ErreurPortail(0, 'hors_perimetre', `Refusé : ${chemin} ne vise pas le BFF.`);
            }
            /* La garde ci-dessus borne la FORME transmise ; pas ce que la résolution
               d'URL en ferait. « /portail/../api/x » la passe, puis `new URL`
               normalise les « .. » (y compris encodés « %2e%2e ») et la requête
               sortirait du périmètre — avec les cookies du BFF. On borne donc aussi
               le RÉSOLU. Aucun chemin réel de ce paquet ne contient de segment
               relatif (tous littéraux) : le refus ne gêne aucun appelant légitime. */
            const resolu = new URL(chemin, base ?? 'http://perimetre.invalid');
            if (!resolu.pathname.startsWith('/portail/')) {
                throw new ErreurPortail(0, 'hors_perimetre', `Refusé : ${chemin} sort du périmètre une fois résolu.`);
            }
            const faire = options.appeler ?? fetch;
            const entetes = {};
            if (options.corps !== undefined)
                entetes['content-type'] = 'application/json';
            // La désignation du slot — le BFF n'accepte sa valeur que jointe au
            // scellé de CETTE requête : la poser ne transporte aucun pouvoir.
            if (options.slotRef !== undefined)
                entetes['x-betool-slot'] = options.slotRef;
            // Le double-submit ne vaut que sur ce qui ÉCRIT : le BFF ne l'exige pas
            // d'une lecture (la lecture par un tiers est hors du navigateur qui la
            // sert — le trousseau ne se prévisualise pas).
            if (methode !== 'GET') {
                const jeton = lireJetonCsrf();
                if (jeton !== null)
                    entetes['x-betool-csrf'] = jeton;
            }
            return faire(base === null ? chemin : resolu.toString(), {
                method: methode,
                headers: entetes,
                body: options.corps === undefined ? undefined : JSON.stringify(options.corps),
                signal: options.signal ?? null,
                // Même origine : `same-origin`, explicite. Origine sœur autorisée :
                // `include`, sinon le navigateur ne joindrait jamais le trousseau —
                // et sans lui, la requête ne résout AUCUN slot (le seul pouvoir est
                // dans la coïncidence cookie + désignation, côté BFF).
                credentials: base === null ? 'same-origin' : 'include',
            });
        },
    };
}
/**
 * Lit le corps JSON d'une réponse, ou null si la réponse est vide.
 *
 * Le BFF ne rend jamais de texte libre sur ses routes d'API — un 204 (corps
 * vide) est un succès possible ; tenter de l'analyser en JSON échouerait pour
 * une raison qui n'est pas la sienne.
 */
export async function lireCorps(reponse) {
    if (reponse.status === 204 || reponse.status === 304)
        return null;
    const texte = await reponse.text();
    if (texte === '')
        return null;
    try {
        return JSON.parse(texte);
    }
    catch {
        return null;
    }
}
/** Extrait `{error, code}` du corps d'un refus du BFF ; sinon un code générique qui ne prétend rien. */
export function refusDe(reponse, corps) {
    const p = corps !== null && typeof corps === 'object'
        ? corps
        : {};
    return new ErreurPortail(reponse.status, typeof p.code === 'string' ? p.code : `http_${reponse.status}`, typeof p.error === 'string' ? p.error : `Le BFF a refusé (${reponse.status}).`);
}
//# sourceMappingURL=transport.js.map